import { Voucher } from "./types";

// ========================
// BANK RECONCILIATION TYPES
// ========================

export interface BankTransaction {
  id: string;
  date: string;
  description: string;
  reference: string;
  debit: number;  // withdrawal
  credit: number; // deposit
  balance: number;
  matchedVoucherId?: string;
  status: "Unmatched" | "Matched" | "Reconciled";
}

export interface ReconciliationSummary {
  bankBalance: number;
  ledgerBalance: number;
  difference: number;
  totalTransactions: number;
  matched: number;
  unmatched: number;
  reconciled: number;
}

// ========================
// CSV / EXCEL PARSER
// ========================

export function parseBankCSV(csvText: string): BankTransaction[] {
  const lines = csvText.trim().split("\n");
  if (lines.length < 2) return [];

  const headers = lines[0].toLowerCase().split(",").map((h) => h.trim().replace(/"/g, ""));

  const dateIdx = headers.findIndex((h) => h.includes("date"));
  const descIdx = headers.findIndex((h) => h.includes("desc") || h.includes("narration") || h.includes("particular"));
  const refIdx = headers.findIndex((h) => h.includes("ref") || h.includes("chq") || h.includes("cheque"));
  const debitIdx = headers.findIndex((h) => h.includes("debit") || h.includes("withdrawal") || h.includes("dr"));
  const creditIdx = headers.findIndex((h) => h.includes("credit") || h.includes("deposit") || h.includes("cr"));
  const balanceIdx = headers.findIndex((h) => h.includes("balance") || h.includes("bal"));

  const transactions: BankTransaction[] = [];
  let runningBalance = 0;

  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(",").map((c) => c.trim().replace(/"/g, ""));
    if (cols.length < 3) continue;

    const debit = Math.abs(parseFloat(cols[debitIdx] || "0") || 0);
    const credit = Math.abs(parseFloat(cols[creditIdx] || "0") || 0);
    const balance = balanceIdx >= 0 ? parseFloat(cols[balanceIdx] || "0") || 0 : (runningBalance + credit - debit);
    runningBalance = balance;

    transactions.push({
      id: `bt-${crypto.randomUUID().slice(0, 8)}`,
      date: cols[dateIdx] || "",
      description: cols[descIdx >= 0 ? descIdx : 1] || "",
      reference: cols[refIdx >= 0 ? refIdx : 0] || "",
      debit,
      credit,
      balance,
      status: "Unmatched",
    });
  }

  return transactions;
}

// ========================
// AUTO-MATCHING ENGINE
// ========================

export function autoMatchTransactions(
  bankTxns: BankTransaction[],
  vouchers: Voucher[]
): BankTransaction[] {
  const bankLedgerVouchers = vouchers.filter(
    (v) => ["Receipt", "Payment", "Contra"].includes(v.type) && v.status !== "Cancelled"
  );

  return bankTxns.map((txn) => {
    if (txn.status === "Reconciled") return txn;

    const txnAmount = txn.credit > 0 ? txn.credit : txn.debit;

    // Match by amount + date proximity
    const match = bankLedgerVouchers.find((v) => {
      const voucherAmount = v.totalDebit;
      const amountMatch = Math.abs(voucherAmount - txnAmount) < 0.01;
      if (!amountMatch) return false;

      // Check date within 3 days
      const txnDate = new Date(txn.date);
      const vDate = new Date(v.date);
      const daysDiff = Math.abs((txnDate.getTime() - vDate.getTime()) / (1000 * 60 * 60 * 24));
      return daysDiff <= 3;
    });

    if (match) {
      return { ...txn, matchedVoucherId: match.id, status: "Matched" as const };
    }
    return txn;
  });
}

export function calculateReconciliationSummary(
  bankTxns: BankTransaction[],
  ledgerBalance: number
): ReconciliationSummary {
  const bankBalance = bankTxns.length > 0 ? bankTxns[bankTxns.length - 1].balance : 0;
  const matched = bankTxns.filter((t) => t.status === "Matched").length;
  const reconciled = bankTxns.filter((t) => t.status === "Reconciled").length;
  const unmatched = bankTxns.filter((t) => t.status === "Unmatched").length;

  return {
    bankBalance,
    ledgerBalance,
    difference: Math.abs(bankBalance - ledgerBalance),
    totalTransactions: bankTxns.length,
    matched,
    unmatched,
    reconciled,
  };
}
